import { mapMessageTemplateId, mapMessageTemplates } from "../mappers/messageTemplateMapper";
import {
	GetRequest,
	DeleteRequest,
	UpdateRequest,
} from "../../../../../services/requestHelpers";
import { HttpMethods } from "../../../../../utils/constants";
import { DisplayMessage } from "../../../../../services/helpers";
import { mapMessageTemplateUseCases } from "../mappers/messageTemplateUseCasesMapper";
import {
	buildCreateMessageTemplateRequest,
	buildUpdateMessageTemplateRequest,
} from "../utils/requestBuilders";
import { BrandsWithObjectTypeUrl } from "../../../../../constants/apiUrls";

export const baseUrl = "/api/mobopro/v1/messagetemplates";

export const getMessageTemplates = async (outletId = 0, brandId = 0) => {
	try {
		let url = baseUrl;

		const params = new URLSearchParams();
		if (outletId !== 0) {
			params.append("outletId", outletId);
		}
		if (brandId !== 0) {
			params.append("brandId", brandId);
		}

		const queryString = params.toString();
		if (queryString) {
			url += `?${queryString}`;
		}

		const response = await GetRequest(url);

		if (!response.ok) {
			const errorData = await response.json();
			if (errorData && errorData.errors) {
				errorData.errors.forEach((error) => DisplayMessage(error, "error"));
			} else {
				DisplayMessage("Failed to fetch message templates", "error");
			}
			return undefined;
		}

		const data = await response.json();
		return mapMessageTemplates(data);
	} catch (error) {
		DisplayMessage(
			`Error fetching message templates: ${error.message}`,
			"error"
		);
		return undefined;
	}
};

export const getMessageTemplateById = async (id) => {
	const response = await GetRequest(`${baseUrl}/${id}`);

	if (!response.ok) {
		return undefined;
	}

	return await response.json();
};

export const getBrands = async (objectType) => {
	const url = BrandsWithObjectTypeUrl(objectType);
	const response = await GetRequest(url);

	if (!response.ok) {
		const errorData = await response.json();
		if (errorData && errorData.errors) {
			errorData.errors.forEach((error) => DisplayMessage(error, "error"));
		}
		return undefined;
	}

	const result = await response.json();
	return result;
};

export const deleteMessageTemplate = async (id) => {
	const response = await DeleteRequest(`${baseUrl}/${id}`);

	if (!response.ok) {
		response.json().then((result) => {
			result.errors.forEach((error) => DisplayMessage(error, "error"));
		});
	}

	return response.ok;
};

export const copyMessageTemplate = async (data, templateId) => {
	try {
		const response = await UpdateRequest(
			`${baseUrl}/${templateId}`,
			data,
			HttpMethods.Post
		);

		if (response.ok) {
			return {
				success: true,
				data: mapMessageTemplateId(await response.json()),
			};
		}

		const errorData = await response.json();

		if (errorData?.errors?.includes("MessageTemplateAlreadyExists")) {
			return {
				success: false,
				error: "MessageTemplateAlreadyExists",
			};
		}

		return {
			success: false,
			errors: errorData?.errors,
		};
	} catch (error) {
		console.error("Error copying message template:", error);
		return {
			success: false,
			error: error.message,
		};
	}
};

export const getMessageTemplateUseCases = async (objectType, messageType) => {
	try {
		let url = "/api/mobopro/v1/messagetemplateusecases";

		const params = new URLSearchParams();
		if (objectType) {
			params.append("objectType", objectType);
		}
		if (messageType) {
			params.append("messageType", messageType);
		}

		if (params.toString()) {
			url += `?${params.toString()}`;
		}

		const response = await GetRequest(url);

		if (!response.ok) {
			const errorData = await response.json();
			if (errorData && errorData.errors) {
				errorData.errors.forEach((error) => DisplayMessage(error, "error"));
			}
			return [];
		}

		const data = await response.json();
		return mapMessageTemplateUseCases(data);
	} catch (error) {
		DisplayMessage(
			`Error fetching message template use cases: ${error.message}`,
			"error"
		);
		return [];
	}
};

export const createMessageTemplate = async (data) => {
	try {
		const requestBody = buildCreateMessageTemplateRequest(data);
		const requestData = JSON.stringify(requestBody);

		const response = await UpdateRequest(
			baseUrl,
			requestData,
			HttpMethods.Post
		);
		const result = await response.json();

		if (!response.ok) {
			if (result.errors) {
				result.errors.forEach((error) => DisplayMessage(error, "error"));
			} else {
				DisplayMessage("Failed to create message template", "error");
			}
			return { success: false, errors: result.errors };
		}

		return { success: true, data: result };
	} catch (error) {
		DisplayMessage(
			`Error creating message template: ${error.message}`,
			"error"
		);
		return { success: false, error: error.message };
	}
};

export const updateMessageTemplate = async (templateId, data) => {
	try {
		const requestBody = buildUpdateMessageTemplateRequest(data);
		const requestData = JSON.stringify(requestBody);

		const response = await UpdateRequest(
			`${baseUrl}/${templateId}`,
			requestData,
			HttpMethods.Patch
		);
		const result = await response.json();

		if (!response.ok) {
			if (result.errors) {
				result.errors.forEach((error) => DisplayMessage(error, "error"));
			} else {
				DisplayMessage("Failed to update message template", "error");
			}
			return { success: false, errors: result.errors };
		}

		return { success: true, data: result };
	} catch (error) {
		DisplayMessage(
			`Error updating message template: ${error.message}`,
			"error"
		);
		return { success: false, error: error.message };
	}
};
